import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const Navbar = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(() => location.pathname !== "/");
  const headerRef = useRef<HTMLElement | null>(null);

  const isActive = (path: string) => {
    if (path === "/playlists") return location.pathname === "/playlists" || location.pathname.startsWith("/post/playlist");
    if (path === "/articulos") {
      return (
        location.pathname === "/articulos" ||
        location.pathname === "/blogs" ||
        location.pathname.startsWith("/post/articulos") ||
        location.pathname.startsWith("/post/blog-legacy")
      );
    }
    return location.pathname === path;
  };

  const isHome = location.pathname === "/";

  useEffect(() => {
    if (!isHome) {
      setHeaderVisible(true);
      return;
    }

    setHeaderVisible(false);

    const update = () => {
      const el = document.getElementById("home-hero");
      if (!el) {
        setHeaderVisible(true);
        return;
      }
      setHeaderVisible(el.getBoundingClientRect().bottom <= 2);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [isHome]);

  useEffect(() => {
    if (isHome && !headerVisible) setMobileOpen(false);
  }, [isHome, headerVisible]);

  useEffect(() => {
    if (!mobileOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const root = headerRef.current;
      if (!root) return;
      const target = event.target as Node | null;
      if (target && !root.contains(target)) {
        setMobileOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [mobileOpen]);

  const links = [
    { path: "/", label: "Inicio" },
    { path: "/feed", label: "Conciertos" },
    { path: "/playlists", label: "Playlists" },
    { path: "/articulos", label: "Artículos" },
    { path: "/contacto", label: "Contacto" },
  ];

  return (
    <header
      ref={headerRef}
      className={cn(
        "fixed top-0 left-0 right-0 z-50 border-b border-border bg-background transition-transform duration-500 ease-out",
        isHome && !headerVisible && "-translate-y-full pointer-events-none",
        (!isHome || headerVisible) && "shadow-sm",
      )}
    >
      <div className="mx-auto flex h-14 max-w-[80rem] items-center justify-between px-5 md:h-16 md:px-9">
        <Link to="/" className="flex items-center gap-3" aria-label="Popistant — inicio">
          <img
            src="/logo-256.webp"
            alt=""
            className="h-8 w-8 rounded-full object-cover ring-1 ring-border"
            width={32}
            height={32}
            aria-hidden
          />
          <span className="font-display text-lg font-bold uppercase tracking-[0.08em] text-foreground">
            Popistant
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={cn(
                "rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                isActive(link.path) ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {link.label}
            </Link>
          ))}
          {isAdmin && (
            <Link
              to="/admin"
              className={cn(
                "rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                isActive("/admin") ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              Admin
            </Link>
          )}
        </nav>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-foreground md:hidden"
          aria-label={mobileOpen ? "Cerrar menu" : "Abrir menu"}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            {mobileOpen ? (
              <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            ) : (
              <>
                <path d="M3 5H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <path d="M3 10H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <path d="M3 15H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </>
            )}
          </svg>
        </button>
      </div>

      {mobileOpen && (
        <nav className="border-t border-border bg-background px-4 pb-4 pt-2 md:hidden">
          {links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              className={cn(
                "block rounded-lg px-3 py-2.5 text-sm transition-colors",
                isActive(link.path) ? "font-semibold text-foreground" : "text-muted-foreground",
              )}
            >
              {link.label}
            </Link>
          ))}
          {isAdmin && (
            <Link
              to="/admin"
              onClick={() => setMobileOpen(false)}
              className={cn(
                "block rounded-lg px-3 py-2.5 text-sm transition-colors",
                isActive("/admin") ? "font-semibold text-foreground" : "text-muted-foreground",
              )}
            >
              Admin
            </Link>
          )}
        </nav>
      )}
    </header>
  );
};

export default Navbar;
